import React, { useContext } from "react";

import classNames from "classnames";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { noop } from "utils/noop";
import {
  Root,
  Content,
  Overlay,
  Title,
  Portal,
  Description,
} from "@radix-ui/react-dialog";

import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import Button from "ui/Button";

export type ModalLayout = "right sidebar" | "centered";

const ModalCloseContext = React.createContext(noop);

export type ModalContentProps =
  | RightModalContentProps
  | CenterModalContentProps;

type ModalSharedContentProps = {
  className?: string;
  heading: React.ReactNode;
  children: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
};

type RightModalContentProps = ModalSharedContentProps & {
  layout: "right sidebar";
  width?: string;
};

type CenterModalContentProps = ModalSharedContentProps & {
  layout: "centered";
};

export const ModalContent = ({ layout, ...rest }: ModalContentProps) => {
  switch (layout) {
    case "centered":
      return <CenteredModalContent {...rest} />;
    case "right sidebar":
      return <RightSidebarModalContent {...rest} />;
    default:
      return null;
  }
};

export const CenteredModalContent = ({
  title,
  description,
  heading,
  children,
  className,
}: Omit<CenterModalContentProps, "layout">) => {
  const baseClasses =
    "isolate z-[101] bg-white shadow-md flex flex-col min-w-[60%] mx-5 md:mx-10 my-5 max-h-[calc(100dvh-100px)]";

  const cn = classNames(baseClasses, className);
  return (
    <div className="grid absolute top-0 right-0 bottom-0 left-0 place-items-center">
      <Content className={cn}>
        <VisuallyHidden>
          <Title>{title}</Title>
          <Description>{description ?? title}</Description>
        </VisuallyHidden>
        {heading}
        <div className="overflow-auto flex-shrink">
          <div className="m-5">{children}</div>
        </div>
      </Content>
    </div>
  );
};

export const RightSidebarModalContent = ({
  title,
  description,
  heading,
  children,
  className,
  width,
}: Omit<RightModalContentProps, "layout">) => {
  const baseClasses = classNames(
    "isolate z-[101] bg-white shadow-md flex flex-col absolute top-0 right-0 bottom-0",
    // Fill (minus a 300px gutter) by default; when a width is given, anchor to
    // the right at that width instead.
    width ? "max-w-full" : "left-0 md:ml-[300px]",
  );

  const cn = classNames(baseClasses, className);
  return (
    <Content className={cn} style={width ? { width } : undefined}>
      <VisuallyHidden>
        <Title>{title}</Title>
        <Description>{description ?? title}</Description>
      </VisuallyHidden>
      {heading}
      <div className="my-2 overflow-auto flex-grow flex-shrink basis-0">
        <div className="mx-5">{children}</div>
      </div>
    </Content>
  );
};

export const ModalOverlay = ({
  children,
  onDismiss,
}: {
  onDismiss: () => void;
  children: React.ReactNode;
}) => {
  const cn =
    "fixed top-0 right-0 bottom-0 left-0 overflow-auto isolate z-10 bg-[#000000AF]";

  return (
    <Root open onOpenChange={onDismiss}>
      <Portal>
        <Overlay className={cn} />
        <ModalCloseContext.Provider value={onDismiss}>
          {children}
        </ModalCloseContext.Provider>
      </Portal>
    </Root>
  );
};

export const ModalCloseButton = ({ className }: { className?: string }) => {
  const onDismiss = useContext(ModalCloseContext);
  return (
    <Button bare className={className} onClick={onDismiss}>
      <FontAwesomeIcon icon={faTimes} />
    </Button>
  );
};
